import { BedDouble, Hotel, Phone, Clock, Users } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { Card, Avatar, Badge, Loader, EmptyState, SectionTitle } from '../../components/UI';
import { NotIncluded } from './shared';

export default function AttendeeStay() {
  const { eventId, hasModule } = useEvents();
  const included = hasModule('hotelBooking');
  const { data: room, loading } = useFetch('/rooms/my', { event: eventId }, { skip: !eventId || !included });

  if (!included) return <><SectionTitle title="Your stay" /><NotIncluded what="Hotel stay" icon={BedDouble} /></>;
  if (loading) return <Loader full label="Loading your stay" />;
  if (!room) {
    return (
      <>
        <SectionTitle title="Your stay" />
        <EmptyState message="Room not allocated yet" hint="Your room and partners appear here once the hotel allocates them." icon={BedDouble} />
      </>
    );
  }

  const hotel = room.hotel || {};
  const address = [hotel.address?.line1, hotel.address?.city, hotel.address?.state].filter(Boolean).join(', ');
  const partners = room.partners || [];

  return (
    <>
      <SectionTitle title="Your stay" subtitle={hotel.name} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        <Card padding={24} style={{ textAlign: 'center', background: 'linear-gradient(135deg, #1A1A22, #121218)' }}>
          <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Room</p>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 52, fontWeight: 700, color: room.roomNumber ? 'var(--gold-300)' : 'var(--text-lo)', marginTop: 4 }}>
            {room.roomNumber || 'Pending'}
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {[room.category, room.floor ? `Floor ${room.floor}` : null].filter(Boolean).join(' · ') || 'Details to follow'}
          </p>
          {room.groupName && <Badge tone="gold" style={{ marginTop: 12 }}>{room.groupName}</Badge>}
        </Card>

        <Card padding={22}>
          <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Users size={15} color="var(--gold-500)" />
            {partners.length ? 'Sharing with' : 'Single occupancy'}
          </p>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 11 }}>
            {partners.map((p) => (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <Avatar name={p.name} size={34} />
                <div>
                  <p style={{ fontSize: 13.5, fontWeight: 500 }}>{p.name}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>{p.designation || 'Attendee'}</p>
                </div>
              </div>
            ))}
            {!partners.length && <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>You have the room to yourself.</p>}
          </div>
        </Card>

        <Card padding={22}>
          <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Hotel size={15} color="var(--gold-500)" />{hotel.name || 'Hotel'}</p>
          {address && <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 8, lineHeight: 1.6 }}>{address}</p>}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12, fontSize: 12.8, color: 'var(--text-mid)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={13} />Check-in {hotel.checkInTime || '14:00'}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={13} />Check-out {hotel.checkOutTime || '12:00'}</span>
          </div>
          {hotel.contactMobile && (
            <a href={`tel:${hotel.contactMobile}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 14, fontSize: 13, color: 'var(--gold-300)' }}>
              <Phone size={14} /> {hotel.contactMobile}
            </a>
          )}
        </Card>
      </div>
    </>
  );
}
